import { createConsola } from "consola";
import type {
  ConsolaInstance,
  PromptOptions,
  InputLogObject,
  ConsolaReporter,
  LogObject,
  ConsolaOptions,
  LogType,
} from "consola";
import { DEFAULT_OPTIONS } from "./constants";
import type { LoggerOptions, LogContext, Reporter } from "./types";
import {
  LoggerError,
  SerializationError,
  ConfigurationError,
  defaultErrorHandler,
  ErrorHandler,
} from "./errors";
import { LOG_LEVELS, type LogLevel } from "./constants";

// Add debug logging
const DEBUG =
  process.env.DEBUG === "true" && process.env.NODE_ENV === "development";

function debugLog(...args: any[]) {
  if (DEBUG) {
    console.log("[LOGGER_DEBUG]", ...args);
  }
}

interface LogFn {
  (message: InputLogObject | any, ...args: any[]): void;
  raw: (...args: any[]) => void;
}

type SelectOption = {
  label: string;
  value: string;
  hint?: string;
};

export class Logger {
  private static instance: Logger;
  private consola: ConsolaInstance;

  private reporters: Reporter[] = [];
  private errorHandler: ErrorHandler;
  private static loggerCache = new Map<string, Logger>();

  private options: LoggerOptions;
  private readonly defaultLabels: Record<string, string>;

  private constructor(
    options: LoggerOptions = {},
    errorHandler?: ErrorHandler
  ) {
    this.errorHandler = errorHandler ?? defaultErrorHandler;

    try {
      debugLog(
        "Logger constructor called with options:",
        JSON.stringify(options, null, 2)
      );
      this.options = {
        ...DEFAULT_OPTIONS,
        ...options,
        context: options.context || {},
        level: options.level ?? DEFAULT_OPTIONS.level,
      };

      // Store custom reporters if provided
      if (options.reporters) {
        debugLog("Setting reporters from options:", options.reporters);
        this.reporters = options.reporters as unknown as Reporter[];
      }
      debugLog("Reporters after initialization:", this.reporters);

      // Create consola instance with our options
      // But don't pass reporters to consola - we'll handle them ourselves
      const consolaOptions = {
        ...this.options,
        level: this.options.level,
        formatOptions: {
          ...DEFAULT_OPTIONS.formatOptions,
          ...options.formatOptions,
        },
        // Don't pass reporters to consola - we'll handle them ourselves
        reporters: [],
      };

      this.reporters = (options.reporters as ConsolaReporter[]) || [];

      this.defaultLabels = {
        service: process.env.WORKER_TYPE || "unknown",
        version: process.env.WORKER_VERSION || "unknown",
        environment: process.env.NODE_ENV || "development",
      };

      debugLog(
        "Creating consola instance with options:",
        JSON.stringify(consolaOptions, null, 2)
      );
      this.consola = createConsola(consolaOptions);
    } catch (error) {
      const configError = new ConfigurationError(
        "Failed to initialize logger",
        error instanceof Error ? error : undefined
      );
      this.errorHandler(configError);
      throw configError;
    }

    // Added: Override error method to wrap non-LoggerError errors.
    const originalError = this.error;
    this.error = ((...args: [any, ...any[]]) => {
      const idx = args.findIndex(
        (arg) => arg instanceof Error && !(arg instanceof LoggerError)
      );
      if (idx >= 0) {
        args[idx] = new LoggerError(args[idx].message, args[idx]);
      }
      return originalError.apply(this, args);
    }) as LogFn;
  }

  private safeClone(obj: any): any {
    if (!obj || obj instanceof Error) return obj;

    try {
      const cache = new WeakSet();
      return JSON.parse(
        JSON.stringify(obj, (_, value) => {
          if (typeof value === "object" && value !== null) {
            if (cache.has(value)) {
              const error = new SerializationError(
                "Circular reference detected",
                obj
              );
              this.errorHandler(error);
              return "[Circular]";
            }
            cache.add(value);
          }
          return value;
        })
      );
    } catch (err) {
      const error = new SerializationError(
        "Failed to serialize object",
        obj,
        err instanceof Error ? err : undefined
      );
      this.errorHandler(error);
      return String(obj);
    }
  }

  private formatLogEntry(
    level: string,
    message: unknown,
    args: unknown[] = [],
    error?: Error
  ): LogObject {
    try {
      // Format for Consola's expected LogObject format
      const entry = {
        // Date object for timestamp
        date: new Date(),

        // Type matches the level
        type: level as LogType,

        // Level as number from LOG_LEVELS
        level: LOG_LEVELS[level as keyof typeof LOG_LEVELS] || 3,

        // Tag from context if available
        tag: this.options.context?.serviceName || "",

        // Convert message to string
        message: this.formatMessage(message),

        // Arguments array
        args: error
          ? [error, ...args.filter((arg) => !(arg instanceof Error))]
          : args,

        // Include additional context for our custom needs
        context: this.options.context || {},

        // Include labels for backward compatibility
        labels: {
          ...this.defaultLabels,
          level,
          // Add service name from context if available
          service:
            this.options.context?.serviceName || this.defaultLabels.service,
          // Add environment from context if available
          environment:
            this.options.context?.environment || this.defaultLabels.environment,
        },
      } as LogObject;

      return entry;
    } catch (err) {
      // Fallback if formatting fails
      return {
        date: new Date(),
        additional: undefined,
        type: level as LogType,
        level: LOG_LEVELS[level as keyof typeof LOG_LEVELS] || 3,
        tag: "",
        message: String(message),
        args: error ? [error] : [],
        context: this.options.context || {},
      };
    }
  }

  private formatMessage(message: unknown): string {
    if (message instanceof Error) {
      return message.message;
    }

    if (typeof message === "object" && message !== null) {
      return (message as any).message || JSON.stringify(message);
    }
    return String(message);
  }

  private formatData(args: unknown[], error?: Error): Record<string, unknown> {
    const data: Record<string, unknown> = {};

    // Include error details if present
    if (error) {
      data.error = {
        name: error.name,
        message: error.message,
        stack: error.stack,
        ...(error as any).context,
      };
    }

    // Include additional arguments
    if (args.length > 0) {
      data.args = args.map((arg) => this.sanitizeValue(arg));
    }

    return data;
  }

  private shouldLog(level: keyof typeof LOG_LEVELS): boolean {
    const logLevel = LOG_LEVELS[level];
    const currentLevel = this.consola.level;

    // Handle special cases
    if (currentLevel === LOG_LEVELS.SILENT) {
      return false;
    }
    if (currentLevel === LOG_LEVELS.VERBOSE) {
      return true;
    }

    // For all other levels, only log if the message level is less than or equal to current level
    return logLevel <= currentLevel;
  }

  private sanitizeContext(context: LogContext): Record<string, unknown> {
    return this.sanitizeValue(context) as Record<string, unknown>;
  }

  private sanitizeValue(value: unknown): unknown {
    if (!value) return value;

    if (value instanceof Error) {
      return {
        name: value.name,
        message: value.message,
        stack: value.stack,
      };
    }

    try {
      const seen = new WeakSet();
      return JSON.parse(
        JSON.stringify(value, (_, v) => {
          if (typeof v === "object" && v !== null) {
            if (seen.has(v)) return "[Circular]";
            seen.add(v);
          }
          if (v instanceof Set) return Array.from(v);
          if (v instanceof Map) return Object.fromEntries(v);
          if (typeof v === "bigint") return v.toString();
          return v;
        })
      );
    } catch {
      return String(value);
    }
  }

  public static getInstance(options?: LoggerOptions): Logger {
    debugLog("getInstance called with options:", options);
    if (!Logger.instance) {
      debugLog("Creating new Logger instance");
      Logger.instance = new Logger(options);
    } else {
      debugLog("Returning existing Logger instance");
    }
    return Logger.instance;
  }

  /**
   * Create a new logger with additional context
   */
  withContext(context: Record<string, any>): Logger {
    debugLog(`withContext called with: ${JSON.stringify(context)}`);
    debugLog("Creating new logger with merged context");

    // Create new options with merged context
    const newOptions: LoggerOptions = {
      level: this.options.level,
      formatOptions: { ...this.options.formatOptions },
      context: {
        ...this.options.context,
        ...context,
      },
    };

    // Create a new logger instance with the merged context
    const newLogger = new Logger(newOptions);

    debugLog(
      `Setting reporters on new logger: ${JSON.stringify(this.reporters)}`
    );

    // Ensure reporters are properly set on the new logger
    newLogger.reporters = this.reporters as ConsolaReporter[];

    return newLogger;
  }

  public setLevel(level: number): void {
    this.consola.level = level;
    this.options.level = level;
  }

  public async end(): Promise<void> {
    await Promise.all(
      this.reporters.map((reporter) => reporter.end?.()).filter(Boolean)
    );
  }

  public callReporters(logObj: LogObject): void {
    // Create context object required by ConsolaReporter
    const ctx = { options: this.options as ConsolaOptions };

    // Call each reporter
    for (const reporter of this.reporters) {
      if (typeof reporter.log === "function") {
        reporter.log(logObj, ctx);
      }
    }
  }

  public prompt(
    message: string,
    options: PromptOptions | undefined
  ): Promise<string | boolean | SelectOption | (string | SelectOption)[]> {
    return this.consola.prompt(message, options);
  }

  /**
   * Debug level logging
   */
  debug(message: any, ...args: any[]): void {
    if (!this.shouldLog("DEBUG")) return;

    const logObj = this.formatLogEntry("DEBUG", message, args);
    debugLog(`debug method called with message: ${message}`);
    debugLog(`Current reporters: ${this.reporters.length}`);

    this.callReporters(logObj);

    // Call consola with the message and args
    this.consola.debug(message, ...args);
  }

  /**
   * Info level logging
   */
  info(message: any, ...args: any[]): void {
    if (!this.shouldLog("INFO")) return;

    const logObj = this.formatLogEntry("INFO", message, args);

    debugLog(`info method called with message: ${message}`);
    debugLog(`Current reporters: ${this.reporters.length}`);

    this.callReporters(logObj);

    // Call consola with the message and args
    this.consola.info(message, ...args);
  }

  /**
   * Success level logging
   */
  success(message: any, ...args: any[]): void {
    if (!this.shouldLog("SUCCESS")) return;

    const logObj = this.formatLogEntry("SUCCESS", message, args);
    debugLog(`success method called with message: ${message}`);
    debugLog(`Current reporters: ${this.reporters.length}`);

    this.callReporters(logObj);

    // Call consola with the message and args
    this.consola.success(message, ...args);
  }

  /**
   * Warning level logging
   */
  warn(message: any, ...args: any[]): void {
    if (!this.shouldLog("WARN")) return;

    const logObj = this.formatLogEntry("WARN", message, args);
    debugLog(`warn method called with message: ${message}`);
    debugLog(`Current reporters: ${this.reporters.length}`);

    this.callReporters(logObj);

    // Call consola with the message and args
    this.consola.warn(message, ...args);
  }

  /**
   * Error level logging
   */
  error(message: any, ...args: any[]): void {
    if (!this.shouldLog("ERROR")) return;

    const logObj = this.formatLogEntry("ERROR", message, args);
    debugLog(`error method called with message: ${message}`);
    debugLog(`Current reporters: ${this.reporters.length}`);

    this.callReporters(logObj);

    // Call consola with the message and args
    this.consola.error(message, ...args);
  }

  /**
   * Fatal level logging
   */
  fatal(message: any, ...args: any[]): void {
    if (!this.shouldLog("FATAL")) return;

    const logObj = this.formatLogEntry("FATAL", message, args);
    debugLog(`fatal method called with message: ${message}`);
    debugLog(`Current reporters: ${this.reporters.length}`);

    this.callReporters(logObj);

    // Call consola with the message and args
    this.consola.fatal(message, ...args);
  }

  /**
   * Trace level logging
   */
  trace(message: any, ...args: any[]): void {
    if (!this.shouldLog("TRACE")) return;

    const logObj = this.formatLogEntry("TRACE", message, args);
    debugLog(`trace method called with message: ${message}`);
    debugLog(`Current reporters: ${this.reporters.length}`);

    this.callReporters(logObj);

    // Call consola with the message and args
    this.consola.trace(message, ...args);
  }

  /**
   * Generic logging method
   */
  log(message: any, ...args: any[]): void {
    if (!this.shouldLog("LOG")) return;

    const logObj = this.formatLogEntry("LOG", message, args);
    debugLog(`Current reporters: ${this.reporters.length}`);

    this.callReporters(logObj);

    // Call consola with the message and args
    this.consola.log(message, ...args);
  }
}
